// DESCRIPTION: Booking confirmation screen — success state with key details, "what's next"
// DESCRIPTION: copy, and an e-voucher/ticket carrying the booking reference.

import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import { formatTime } from '../calendar/utils';
import { formatDateLong, formatMoney } from './helpers';

/**
 * Compose a Google Calendar "add event" URL from an event + selected slot.
 *
 * Times are passed in the event's own wall-clock time (no UTC conversion) so
 * the event lands at the intended local time regardless of the attendee's
 * browser timezone.
 *
 * @param {Object}  event        Event object.
 * @param {boolean} slotBased    Whether the event uses slot selection.
 * @param {string}  selectedDate ISO date chosen in the wizard (slot-based).
 * @param {Object}  selectedSlot Selected slot { start, end } (slot-based).
 *
 * @return {string} Google Calendar URL, or '' if no date is available.
 */
function buildCalendarUrl( event, slotBased, selectedDate, selectedSlot ) {
	const isoDate = slotBased ? selectedDate : event?.event_date;
	const startRaw = slotBased ? selectedSlot?.start : event?.event_time;
	const endRaw = slotBased ? selectedSlot?.end : event?.end_time;

	if ( ! isoDate || ! startRaw ) {
		return '';
	}

	const fmt = ( d, t ) => {
		if ( ! d || ! t ) {
			return '';
		}
		return `${ d.replace( /-/g, '' ) }T${ t.replace( /:/g, '' ) }00`;
	};

	const start = fmt( isoDate, startRaw );
	const end = fmt( event?.end_date || isoDate, endRaw ) || start;

	const params = new URLSearchParams( {
		action: 'TEMPLATE',
		text: event?.title?.rendered || event?.title || '',
		dates: `${ start }/${ end }`,
	} );

	if ( event?.location ) {
		params.set( 'location', event.location );
	}
	if ( event?.description ) {
		params.set( 'details', event.description.replace( /<[^>]*>/g, '' ) );
	}

	return `https://calendar.google.com/calendar/render?${ params.toString() }`;
}

/**
 * Confirmation — the final step shown after a booking is created/paid.
 *
 * @param {Object}  props
 * @param {Object}  props.event              The event object from the REST API.
 * @param {boolean} props.slotBased          Whether the event uses date/slot selection.
 * @param {string}  props.selectedDate       Selected ISO date (slot-based only).
 * @param {Object}  props.selectedSlot       Selected slot object { start, end } (slot-based only).
 * @param {string}  props.attendeeName       Attendee display name.
 * @param {string}  props.attendeeEmail      Attendee email (confirmation destination).
 * @param {string}  props.bookingRef         Booking reference (UUID), if available.
 * @param {string}  props.variant            'confirmed' | 'manual' | 'payment_pending'.
 * @param {string}  props.manualInstructions Manual payment instructions, when variant is 'manual'.
 *
 * @return {JSX.Element} The confirmation screen.
 */
const Confirmation = ( {
	event,
	slotBased,
	selectedDate,
	selectedSlot,
	attendeeName,
	attendeeEmail,
	bookingRef,
	variant = 'confirmed',
	manualInstructions = '',
} ) => {
	const [ shared, setShared ] = useState( false );

	const price = event ? event.price || 0 : 0;
	const symbol = event ? event.currency_symbol || event.currency || '' : '';

	const calendarUrl = buildCalendarUrl(
		event,
		slotBased,
		selectedDate,
		selectedSlot
	);

	const handleShare = async () => {
		const url = window.location.href;
		const title = event?.title?.rendered || event?.title || '';
		const shareData = {
			title,
			text: __( 'I just booked an event.', 'nemtly-booking' ),
			url,
		};

		if ( navigator.share ) {
			try {
				await navigator.share( shareData );
			} catch ( e ) {
				/* user dismissed — no-op */
			}
			return;
		}

		try {
			await navigator.clipboard.writeText( url );
			setShared( true );
		} catch ( e ) {
			/* clipboard unavailable — no-op */
		}
	};

	const dateLong = slotBased
		? formatDateLong( selectedDate )
		: ( event && event.date ) || '';
	const slotRange = selectedSlot
		? `${ formatTime( selectedSlot.start ) } – ${ formatTime(
				selectedSlot.end
		  ) }`
		: '';
	const timeText = slotBased ? slotRange : ( event && event.time ) || '';

	const isConfirmed = variant === 'confirmed';
	const title = isConfirmed
		? __( "You're booked in!", 'nemtly-booking' )
		: __( 'Booking received!', 'nemtly-booking' );

	const nextText = isConfirmed
		? __(
				"You'll receive a confirmation email with all the details. If the event is online, the joining link will be in that email.",
				'nemtly-booking'
		  )
		: __(
				"We'll confirm your booking by email once your payment is received.",
				'nemtly-booking'
		  );

	return (
		<div className="nb-confirm">
			<div className="nb-confirm__check" aria-hidden="true">
				✓
			</div>
			<h2 className="nb-confirm__title">{ title }</h2>
			<p className="nb-confirm__sub">
				{ __( 'Confirmation sent to', 'nemtly-booking' ) }{ ' ' }
				<strong>
					{ attendeeEmail || __( 'your email', 'nemtly-booking' ) }
				</strong>
			</p>

			<div className="nb-confirm__details">
				{ dateLong && (
					<div className="nb-confirm__row">
						<span className="nb-confirm__icon" aria-hidden="true">
							📅
						</span>
						{ dateLong }
					</div>
				) }
				{ timeText && (
					<div className="nb-confirm__row">
						<span className="nb-confirm__icon" aria-hidden="true">
							🕙
						</span>
						{ timeText }
					</div>
				) }
				{ event && event.location && (
					<div className="nb-confirm__row">
						<span className="nb-confirm__icon" aria-hidden="true">
							📍
						</span>
						{ event.location }
					</div>
				) }
				{ attendeeName && (
					<div className="nb-confirm__row">
						<span className="nb-confirm__icon" aria-hidden="true">
							👤
						</span>
						{ attendeeName }
					</div>
				) }
				{ price > 0 && (
					<div className="nb-confirm__row">
						<span className="nb-confirm__icon" aria-hidden="true">
							💳
						</span>
						{ formatMoney( symbol, price ) } ·{ ' ' }
						{ isConfirmed
							? __( 'Payment succeeded', 'nemtly-booking' )
							: __( 'Payment pending', 'nemtly-booking' ) }
					</div>
				) }
			</div>

			{ variant === 'manual' && manualInstructions && (
				<div className="nb-manual-instructions">
					{ manualInstructions }
				</div>
			) }

			<div className="nb-confirm__next">
				<div className="nb-confirm__next-label">
					{ __( "What's next", 'nemtly-booking' ) }
				</div>
				<div className="nb-confirm__next-text">{ nextText }</div>
			</div>

			<div className="nb-confirm__actions">
				{ calendarUrl && (
					<a
						className="nemtly-button-secondary nemtly-button-md"
						href={ calendarUrl }
						target="_blank"
						rel="noopener noreferrer"
					>
						{ __( 'Add to calendar', 'nemtly-booking' ) }
					</a>
				) }
				<button
					type="button"
					className="nemtly-button-ghost nemtly-button-md"
					onClick={ handleShare }
				>
					{ shared
						? __( 'Link copied', 'nemtly-booking' )
						: __( 'Share', 'nemtly-booking' ) }
				</button>
			</div>

			<div className="nb-voucher">
				<div className="nb-voucher__top">
					<div className="nb-voucher__event">
						{ event
							? event.title?.rendered || event.title || ''
							: '' }
					</div>
					<div className="nb-voucher__org">
						{ __( 'Your booking ticket', 'nemtly-booking' ) }
					</div>
				</div>
				<div className="nb-voucher__body">
					{ dateLong && (
						<div className="nb-voucher__row">
							<span className="nb-voucher__k">
								{ __( 'Date', 'nemtly-booking' ) }
							</span>
							<span className="nb-voucher__v">{ dateLong }</span>
						</div>
					) }
					{ timeText && (
						<div className="nb-voucher__row">
							<span className="nb-voucher__k">
								{ __( 'Time', 'nemtly-booking' ) }
							</span>
							<span className="nb-voucher__v">{ timeText }</span>
						</div>
					) }
					{ event && event.location && (
						<div className="nb-voucher__row">
							<span className="nb-voucher__k">
								{ __( 'Location', 'nemtly-booking' ) }
							</span>
							<span className="nb-voucher__v">
								{ event.location }
							</span>
						</div>
					) }
					{ attendeeName && (
						<div className="nb-voucher__row">
							<span className="nb-voucher__k">
								{ __( 'Attendee', 'nemtly-booking' ) }
							</span>
							<span className="nb-voucher__v">
								{ attendeeName }
							</span>
						</div>
					) }
					{ bookingRef && (
						<div className="nb-voucher__code">
							<div className="nb-voucher__code-lbl">
								{ __( 'Booking reference', 'nemtly-booking' ) }
							</div>
							<div className="nb-voucher__code-val">
								{ bookingRef }
							</div>
						</div>
					) }
				</div>
			</div>
		</div>
	);
};

export default Confirmation;
